import { Component, OnInit, Input, Output, EventEmitter, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GeneralYesNoDialogComponent } from '../dialog/general-yes-no-dialog/general-yes-no-dialog.component';
import { Auth } from '@aws-amplify/auth';
import { Storage } from '@aws-amplify/storage';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import {
  AttachmentItem,
  EventReportForm,
  MasterItem,
  ParticipantAgeRatio,
} from '../model/my-event';
import { HttpClient } from '@angular/common/http';
import { saveAs } from 'file-saver';

type FileUploadCategory = 'eval' | 'image' | 'deliverable' | 'reportDoc';

@Component({
  selector: 'app-my-report-edit-form',
  templateUrl: './my-report-edit-form.component.html',
  styleUrl: './my-report-edit-form.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDialogModule,
  ],
})
export class MyReportEditFormComponent implements OnInit {
  @Input() eventId: string = '';
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('fileInputEval')       fileInputEval!:       ElementRef<HTMLInputElement>;
  @ViewChild('fileInputImage')      fileInputImage!:      ElementRef<HTMLInputElement>;
  @ViewChild('fileInputDeliverable') fileInputDeliverable!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInputReportDoc')  fileInputReportDoc!:  ElementRef<HTMLInputElement>;

  showSpinner = true;
  isSaving = false;

  form: EventReportForm = this.createEmptyForm();

  // マスタデータ（仮キー ─ バックエンドAPI確定後に更新）
  masterData: { [key: string]: MasterItem[] } = {};

  // 企画書から自動反映する生ID（マスタロード後に名称解決）
  private _rawCategorySub: (number | null)[] = [null, null, null];
  private _rawImprovementOutcome: number | null = null;
  private _rawImprovementState: (number | null)[] = [null, null, null];
  private _planImplementationMethod: number | null = null;
  targetSelectedNodes: any[] = [];

  private orgMasterData: any = {};

  private officialPositionItems: MasterItem[] = [];
  // 画像ライトボックス
  lightboxUrl: string | null = null;

  openLightbox(url: string): void {
    this.lightboxUrl = url;
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
  }
  private _savedAgreementContent: string = '';
  private _agreementContentWasVisible: boolean = true;

  private masterCountData : any = {};

  // 添付ファイル（種別ごと）
  attachments: Record<FileUploadCategory, AttachmentItem[]> = {
    eval: [],
    image: [],
    deliverable: [],
    reportDoc: [],
  };
  uploadingCategory: FileUploadCategory | null = null;

  private readonly allowedDocExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'ppt', 'pptx', 'xls', 'xlsx', 'doc', 'docx'];
  private readonly allowedImageExtensions = ['jpg', 'jpeg', 'png'];
  private readonly maxDocSize   = 10 * 1024 * 1024;
  private readonly maxImageSize = 5  * 1024 * 1024;

  // 年代%プルダウン選択肢
  readonly agePercentOptions: { value: number | null; label: string }[] = [
    { value: null, label: '' },
    ...[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(v => ({ value: v, label: String(v) })),
  ];

  get implementationStatusOptions(): MasterItem[] {
    return this.getMasterItems('report_implementation_status');
  }

  get negotiationStatusOptions(): MasterItem[] {
    return this.getMasterItems('report_negotiation_status');
  }

  constructor(
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private auth: AuthService,
    private http: HttpClient,
    private dialog: MatDialog,
  ) {}

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit(): void {
    this.loadData();
  }

  // ===== 表示制御 =====

  private get c(): number | null { return this.form.categoryId; }

  /** 延期・中止のとき限定表示（マスタ attribute='limited'）*/
  get isLimitedDisplay(): boolean {
    const items = this.getMasterItems('report_implementation_status');
    const selected = items.find(m => m.id === this.form.implementationStatus);
    return selected?.attribute === 'limited';
  }

  /** 問題の有無（カテゴリー9のみ） */
  get showProblem(): boolean { return this.c === 9; }

  /** 関係者情報・課題/気づき（カテゴリー5, 7） */
  get showStakeholderAndChallenges(): boolean {
    return this.c === 5 || this.c === 7;
  }

  /** 実施内容（カテゴリー6のみ） */
  get showOrgActivity(): boolean { return this.c === 6; }

  /** 実費費用（カテゴリー1,2,3,9,10） */
  get showCost(): boolean {
    return this.c !== null && [1, 2, 3, 9, 10].includes(this.c);
  }

  /** 参加者総数（カテゴリー1,2,3,5,7,9,10） */
  get showTotalParticipants(): boolean {
    return this.c !== null && [1, 2, 3, 5, 7, 9, 10].includes(this.c);
  }

  /** 参加者詳細（年代/女性比率/評価内訳）（カテゴリー1,2,3,10） */
  get showParticipantDetail(): boolean {
    return this.c !== null && [1, 2, 3, 10].includes(this.c);
  }

  /** 改善したい指標（企画の成果）・実施結果/成果（カテゴリー1,2,3,5,6,7,10） */
  get showOutcomeResult(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 10].includes(this.c);
  }

  /** 次回改善点/引き継ぎ事項（カテゴリー1,2,3,5,6,7,10） */
  get showNextImprovement(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 10].includes(this.c);
  }

  /** 労使交渉セクション（カテゴリー8のみ） */
  get showLaborNegotiation(): boolean { return this.c === 8; }

  /** 合意内容（協議状況が attribute='shows_agreement' のとき） */
  get showAgreementContent(): boolean {
    const items = this.getMasterItems('report_negotiation_status');
    const selected = items.find(m => m.id === this.form.negotiationStatus);
    return selected?.attribute === 'shows_agreement';
  }

  /** 改善したい指標（状態の変化）・改善指標結果（カテゴリー1,2,3,5,6,7,8,10） */
  get showImprovementResult(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 8, 10].includes(this.c);
  }

  /** 再参加意向比率・評価セクション（カテゴリー1,2,3,6,10） */
  get showEvaluation(): boolean {
    return this.c !== null && [1, 2, 3, 6, 10].includes(this.c);
  }

  /** 参加者理解度（カテゴリー2のみ） */
  get showUnderstanding(): boolean { return this.c === 2; }

  /** 画像（カテゴリー1,2,3,5,6,7,9,10） */
  get showImages(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 9, 10].includes(this.c);
  }

  /** 参加者評価が「未評価」かどうか */
  get isEvaluationUnrated(): boolean {
    const items = this.getMasterItems('report_eval_score');
    const selected = items.find(m => m.id === this.form.participantEvalScore);
    return selected?.attribute === 'unevaluated';
  }

  // ===== 自動計算 =====

  /** 想定費用との差額表示文字列 */
  get costDiff(): string {
    if (this.form.estimatedTotalCost === null || this.form.actualTotalCost === null) return '';
    const diff = this.form.estimatedTotalCost - this.form.actualTotalCost;
    return diff.toLocaleString() + '円';
  }

  /** 評価収集率表示文字列 */
  get collectionRate(): string {
    if (!this.form.evalCollectedCount || !this.form.participantTotal) return '- %';
    return Math.round(this.form.evalCollectedCount / this.form.participantTotal * 100) + '%';
  }

  /** 年代合計% */
  get ageRatioTotal(): number {
    const r = this.form.ageRatio;
    return (r.under19 || 0) + (r.age20s || 0) + (r.age30s || 0)
         + (r.age40s || 0) + (r.age50s || 0) + (r.over60 || 0);
  }

  /** 年代合計が100%を超えているか */
  get isAgeRatioOver100(): boolean {
    if (!this.showParticipantDetail || this.form.ageRatio.unknown) return false;
    return this.ageRatioTotal > 100;
  }

  get statusLabel(): string {
    return Constant.myEventStatuses[this.form.status] ?? '';
  }

  get coverageRateValue(): string {
    const participants = this.form.participantTotal;
    if (!participants || participants <= 0) return '';

    const denominator = this.targetSelectedNodes.reduce((sum: number, node: any) => {
      return sum + (node.orgCountForCoverage ?? 0);
    }, 0);

    console.log(this.targetSelectedNodes)

    if (denominator <= 0) return '-';

    return Math.round(participants / denominator * 100) + '';
  }

  // ===== マスタ =====

  getMasterItems(type: string): MasterItem[] {
    return this.masterData[type] || [];
  }

  get implementationMethodOptions(): MasterItem[] {
    return this.getMasterItems('implementation_method')
      .filter(m => m.attribute !== 'consultation_only');
  }

  // ===== 優先順位チェックボックス（組合員向け報告方法） =====

  togglePriority(arr: (number | null)[], id: number, max: number): void {
    const idx = arr.indexOf(id);
    // 選択済みの場合は解除し、後続の優先順位を繰り上げる
    if (idx >= 0) {
      arr.splice(idx, 1);
      arr.push(null);
      return;
    }
    if (arr.filter(x => x !== null).length < max) {
      const empty = arr.indexOf(null);
      if (empty >= 0) arr[empty] = id;
    }
  }

  isInPriority(arr: (number | null)[], id: number): boolean {
    return arr.includes(id);
  }

  isPriorityDisabled(arr: (number | null)[], id: number, max: number): boolean {
    return !arr.includes(id) && arr.filter(x => x !== null).length >= max;
  }

  getPriorityNum(arr: (number | null)[], id: number): number | null {
    const idx = arr.indexOf(id);
    return idx >= 0 ? idx + 1 : null;
  }

  resetPriority(arr: (number | null)[]): void {
    arr.fill(null);
  }

  // ===== 協議状況変更 =====
  onNegotiationStatusChange(): void {
    const nowVisible = this.showAgreementContent;
    if (nowVisible && !this._agreementContentWasVisible) {
      this.form.agreementContent = this._savedAgreementContent;
    } else if (!nowVisible && this._agreementContentWasVisible) {
      this._savedAgreementContent = this.form.agreementContent;
      this.form.agreementContent = '';
    }
    // 合意↔一部合意の切り替えは何もしない
    this._agreementContentWasVisible = nowVisible;
  }

  // ===== 年代「不明」チェック =====

  onAgeUnknownChange(): void {
    if (this.form.ageRatio.unknown) {
      this.form.ageRatio.under19 = null;
      this.form.ageRatio.age20s  = null;
      this.form.ageRatio.age30s  = null;
      this.form.ageRatio.age40s  = null;
      this.form.ageRatio.age50s  = null;
      this.form.ageRatio.over60  = null;
    }
  }

  // ===== 数値入力サニタイズ =====

  onNumberInput(event: Event, field: keyof EventReportForm, maxLen: number): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^0-9]/g, '').slice(0, maxLen);
    input.value = cleaned;
    (this.form as any)[field] = cleaned ? parseInt(cleaned, 10) : null;
  }

  onAgeRatioInput(event: Event, key: keyof ParticipantAgeRatio): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^0-9]/g, '').slice(0, 3);
    input.value = cleaned;
    (this.form.ageRatio as any)[key] = cleaned ? parseInt(cleaned, 10) : null;
  }

  // ===== ファイルアップロード =====

  onFileUpload(category: FileUploadCategory): void {
    if (this.uploadingCategory !== null) return;
    const max = 3;
    if (this.attachments[category].length >= max) return;

    const input = this.getFileInput(category);
    if (!input) return;
    input.nativeElement.value = '';
    input.nativeElement.click();
  }

  onFileSelected(event: Event, category: FileUploadCategory): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isImage = category === 'image';
    const allowedExts = isImage ? this.allowedImageExtensions : this.allowedDocExtensions;
    const maxSize = isImage ? this.maxImageSize : this.maxDocSize;

    if (!allowedExts.includes(ext)) {
      this.toastUtil.showErrorToast('', Constant.msgErrorInvalidFile, Constant.toastShowMiliSecErr);
      return;
    }
    if (file.size > maxSize) {
      this.toastUtil.showErrorToast('', Constant.msgErrorLargeFile, Constant.toastShowMiliSecErr);
      return;
    }

    this.uploadingCategory = category;
    const s3Cfg = environment.amplify.Storage['myeventAttachment'];
    const s3Config = {
      bucket: s3Cfg.bucket,
      region: s3Cfg.region,
      level: s3Cfg.level as 'private' | 'public' | 'protected',
      contentType: file.type,
    };

    Auth.currentUserCredentials()
      .then(credentials => {
        const fileKey = Date.now().toString() + '.' + ext;
        return Storage.put(fileKey, file, s3Config).then(result => ({
          filePath: credentials.identityId + '/' + (result as any)['key'],
          fileKey: (result as any)['key'],
        }));
      })
      .then(({ filePath, fileKey }) => {
        const getConfig = {
          bucket: s3Cfg.bucket,
          region: s3Cfg.region,
          level: s3Cfg.level as 'private' | 'public' | 'protected',
          expires: 3600,
        };
        return Storage.get(fileKey, getConfig).then((downloadUrl: any) => {
          this.attachments[category].push({ fileName: file.name, filePath, fileKey, downloadUrl });
        });
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        this.toastUtil.showErrorToast('', Constant.msgErrorUploadFile, Constant.toastShowMiliSecErr);
      })
      .finally(() => {
        this.uploadingCategory = null;
      });
  }

  removeAttachment(category: FileUploadCategory, index: number): void {
    this.attachments[category].splice(index, 1);
  }

  onDownloadAttachment(att: AttachmentItem): void {
    if (!att.downloadUrl) return;
    this.http.get(att.downloadUrl, { responseType: 'arraybuffer' }).subscribe({
      next: data => {
        const blob = new Blob([data], { type: Constant.fileFormatDownload });
        saveAs(blob, att.fileName);
      },
      error: err => {
        this.toastUtil.showErrorToast('', Constant.msgFileDownloadError, Constant.toastShowMiliSec);
        this.commonUtil.debug().log(err);
      },
    });
  }

  private getFileInput(category: FileUploadCategory): ElementRef<HTMLInputElement> | null {
    const map: Record<FileUploadCategory, ElementRef<HTMLInputElement>> = {
      eval:        this.fileInputEval,
      image:       this.fileInputImage,
      deliverable: this.fileInputDeliverable,
      reportDoc:   this.fileInputReportDoc,
    };
    return map[category] ?? null;
  }

  // ===== 保存・キャンセル =====

  onSave(): void {
    if (!this.commonUtil.checkOnline()) return;

    if (this.form.status !== 'draft' && this.form.status !== 'before_draft') {
      const errors = this.validateRequired();
      if (errors.length > 0) {
        this.toastUtil.showErrorToast('', '必須項目が未入力です。', Constant.toastShowMiliSecErr);
        return;
      }
    }

    this.isSaving = true;
    Auth.currentSession()
      .then(session => {
        const options = this.auth.createApiHeader(session);
        options['body'] = this.buildPayload();
        return this.commonUtil.apiPut('/myevent/report/' + this.eventId, options);
      })
      .then(res => {
        if (res.status === Constant.OK) {
          this.toastUtil.showInformationToast('', '保存しました。', Constant.toastShowMiliSec);
        }
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        if (err.message === Constant.msgLoginFailed) {
          this.commonUtil.showReloadDialog(this.auth);
        } else {
          this.toastUtil.showErrorToast('', Constant.msgSaveFailedRetry, Constant.toastShowMiliSecErr);
        }
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  onCancel(): void {
    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      autoFocus: false,
      data: {
        msg: Constant.msgConfirmBack
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cancelled.emit();
      }
    });
  }

  // ===== Private =====

  private loadData(): void {
    this.showSpinner = true;
    Auth.currentSession()
      .then(session => {
        const options = this.auth.createApiHeader(session);
        return Promise.all([
          this.loadMasterData(options),
          this.loadPlanDetail(options),
          this.loadFormData(options),
          this.loadOfficialPositions(options),
        ]);
      })
      .then(() => {
        this.applyDefaults();
        this.showSpinner = false;
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        if (err.message === Constant.msgLoginFailed) {
          this.commonUtil.showReloadDialog(this.auth);
        } else {
          this.showSpinner = false;
          this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
        }
      });
  }

  private loadOfficialPositions(options: object): Promise<void> {
    return this.commonUtil
      .apiGet('/master', options)
      .then(res => {
        const master = res.data?.master ?? {};
        const positions: any[] = master[5] ?? [];
        this.officialPositionItems = positions.map(p => ({
          id: p.id,
          name: p.name,
          parentId: null,
          attribute: null,
        }));
        this.orgMasterData = master;
        this.masterCountData = res.data.master_count;
      });
  }

  private loadMasterData(options: object): Promise<void> {
    return this.commonUtil.apiGet('/myevent/master', options).then(res => {
      this.masterData = res.data;
    });
  }

  private loadPlanDetail(options: object): Promise<void> {
    // レスポンスは MyEventDetail 型: category は名称文字列、categoryId は含まれない
    return this.commonUtil.apiGet('/myevent/edit/' + this.eventId, options).then(res => {
      const d = Array.isArray(res.data) ? res.data[0] : res.data;
      if (!d) return;

      this.form.title              = d.title              ?? '';
      this.form.categoryId         = d.categoryId         ?? null;
      this.form.estimatedTotalCost = d.estimatedTotalCost ?? null;
      this._rawCategorySub           = [d.categorySub1 ?? null, d.categorySub2 ?? null, d.categorySub3 ?? null];
      this._rawImprovementOutcome    = d.improvementIndicatorOutcome ?? null;
      this._rawImprovementState      = [d.improvementIndicatorState1 ?? null, d.improvementIndicatorState2 ?? null, d.improvementIndicatorState3 ?? null];
      this._planImplementationMethod = d.implementationMethod ?? null;
    });
  }

  private buildSelectedNodesFromIds(ids: number[]): any[] {
    if (!ids || ids.length === 0) return [];

    const idSet = new Set(ids);
    const result: any[] = [];

    // #skipを透過して最初の非skipレベルの人数を合計する
    // - #skipノードは透過して子を再帰探索
    // - 非skipノードに到達したらmember_countを取得し、それより下は見ない
    const sumChushinCount = (nodes: any[]): number =>
      nodes.reduce((sum: number, n: any) => {
        if (n.name?.includes('#skip')) {
          return sum + sumChushinCount(n.children ?? []);
        }
        return sum + (n.member_count ?? 0);
      }, 0);

    // 大分類（本部・支部・エリア・専門部）単体の人数を求める
    // - マスタ設定で人数が入力されていればそれを採用（＝下位は考慮しない）
    // - 人数がブランク（null）の場合のみ、中分類以下を合計する
    const daibunruiCount = (masterType: number): number => {
      const headcount = this.masterCountData?.[masterType]?.member_count;
      return headcount != null
        ? headcount
        : sumChushinCount(this.orgMasterData[String(masterType)] ?? []);
    };

    // 全体（先頭固定）: 各大分類の値を合計する
    if (idSet.has(-1)) {
      const zenbuCount = [1, 2, 3, 4].reduce((total, key) => total + daibunruiCount(key), 0);
      result.push({ id: -1, name: '全体', path: '全体', master_type: 0, org_count: zenbuCount, orgCountForCoverage: zenbuCount });
    }

    type Entry = { id: number; name: string; level: number; memberCount: number | null; masterType: number; ancestorIds: number[]; isSkip: boolean; isVirtualRoot: boolean };
    const allEntries: Entry[] = [];

    const virtualRoots = [
      { id: 1, name: '本部',  masterType: 1, key: '1' },
      { id: 2, name: '支部',  masterType: 2, key: '2' },
      { id: 3, name: 'エリア', masterType: 3, key: '3' },
      { id: 4, name: '専門部', masterType: 4, key: '4' },
    ];

    for (const vr of virtualRoots) {
      allEntries.push({ id: vr.id, name: vr.name, level: 0, memberCount: null, masterType: vr.masterType, ancestorIds: [], isSkip: false, isVirtualRoot: true });

      const traverse = (nodes: any[], ancestorIds: number[]) => {
        for (const n of nodes) {
          const isSkip = n.name?.includes('#skip');
          allEntries.push({ id: n.id, name: n.name, level: n.level, memberCount: n.member_count ?? null, masterType: vr.masterType, ancestorIds: [...ancestorIds], isSkip, isVirtualRoot: false });
          if (n.children) traverse(n.children, [...ancestorIds, n.id]);
        }
      };
      traverse(this.orgMasterData[vr.key] ?? [], [vr.id]);
    }

    const selectedEntries = allEntries.filter(e => idSet.has(e.id));
    const selectedIdSet = new Set(selectedEntries.map(e => e.id));

    for (const e of selectedEntries) {
      // 大分類（仮想ルート）が選択済みの場合、その下の中分類は二重カウントしない
      const hasCheckedParentWithOrgCount = e.ancestorIds.some(aid => {
        if (!selectedIdSet.has(aid)) return false;
        const anc = allEntries.find(a => a.id === aid);
        return anc != null && (anc.memberCount != null || anc.isVirtualRoot);
      });

      // その組織「単体」の人数
      // - 大分類: 人数優先（ブランク時のみ中分類以下を合計）
      // - 中分類以下: 自身の member_count
      const ownCount = e.isVirtualRoot
        ? daibunruiCount(e.masterType)
        : (e.memberCount || 0);

      // 上位のマスタが選択済みの場合は下位を二重計上しない
      const orgCountForCoverage = hasCheckedParentWithOrgCount ? 0 : ownCount;

      const pathParts: string[] = [];
      for (const aid of e.ancestorIds) {
        const anc = allEntries.find(a => a.id === aid);
        if (anc && !anc.isSkip) pathParts.push(anc.name);
      }
      if (!e.isSkip) pathParts.push(e.name);

      result.push({ id: e.id, name: e.name, level: e.level, org_count: ownCount, master_type: e.masterType, path: pathParts.join(' '), orgCountForCoverage });
    }

    // 他組織（末尾固定）
    if (idSet.has(-2)) result.push({ id: -2, name: '他組織', path: '他組織', master_type: 0, org_count: null, orgCountForCoverage: 0 });

    return result;
  }

  private loadFormData(options: object): Promise<void> {
    return this.commonUtil.apiGet('/myevent/report/' + this.eventId, options).then(res => {
      const d = Array.isArray(res.data) ? res.data[0] : res.data;
      console.log(res.data)
      if (!d) return;

      // 報告書フィールド
      this.form.status                 = d.status                 ?? 'draft';
      this.form.implementationStatus   = d.implementationStatus   ?? null;
      this.form.implementationMethod   = d.implementationMethod   ?? null;
      this.form.hasProblem             = d.hasProblem             ?? true;
      this.form.problemComment         = d.problemComment         ?? '';
      this.form.stakeholderInfo        = d.stakeholderInfo        ?? '';
      this.form.hasChangeFromPlan      = d.hasChangeFromPlan      ?? true;
      this.form.changeFromPlanComment  = d.changeFromPlanComment  ?? '';
      this.form.actualTotalCost        = d.actualTotalCost        ?? null;
      this.form.costDiffReason         = d.costDiffReason         ?? '';
      this.form.participantTotal       = d.participantTotal       ?? null;
      this.form.participantNew         = d.participantNew         ?? null;
      this.form.ageRatio = {
        under19: d.ageRatioUnder19 ?? null,
        age20s:  d.ageRatio20s     ?? null,
        age30s:  d.ageRatio30s     ?? null,
        age40s:  d.ageRatio40s     ?? null,
        age50s:  d.ageRatio50s     ?? null,
        over60:  d.ageRatioOver60  ?? null,
        unknown: d.ageRatioUnknown ?? false,
      };
      this.form.femaleRatio            = d.femaleRatio            ?? null;
      this.form.implementationContent  = d.implementationContent  ?? '';
      this.form.implementationResult   = d.implementationResult   ?? '';
      this.form.challengesNotes        = d.challengesNotes        ?? '';
      this.form.nextImprovement        = d.nextImprovement        ?? '';
      this.form.negotiationStatus      = d.negotiationStatus      ?? null;
      this.form.agreementContent       = d.agreementContent       ?? '';
      this.form.companyClaim           = d.companyClaim           ?? '';
      this.form.unionClaim             = d.unionClaim             ?? '';
      this.form.unresolvedPoints       = d.unresolvedPoints       ?? '';
      this.form.improvementResultRating = d.improvementResultRating ?? null;
      this.form.improvementResultReason = d.improvementResultReason ?? '';
      this.form.reAttendanceRate        = d.reAttendanceRate       ?? null;
      this.form.participantEvalScore    = d.participantEvalScore   ?? null;
      this.form.evalCollectedCount      = d.evalCollectedCount     ?? null;
      this.form.unevaluatedReason       = d.unevaluatedReason      ?? '';
      this.form.evaluationPart1 = {
        title: d.evaluationPart1Title ?? '',
        score: d.evaluationPart1Score ?? null,
      };
      this.form.evaluationPart2 = {
        title: d.evaluationPart2Title ?? '',
        score: d.evaluationPart2Score ?? null,
      };
      this.form.understandingScore     = d.understandingScore     ?? null;
      this.form.participantOpinions    = d.participantOpinions    ?? '';
      this.form.selfEvalScore          = d.selfEvalScore          ?? null;
      this.form.selfEvalComment        = d.selfEvalComment        ?? '';
      this.form.canRedo                = d.canRedo                ?? null;
      this.form.unionReportMethod = [
        d.unionReportMethod1 ?? null,
        d.unionReportMethod2 ?? null,
        d.unionReportMethod3 ?? null,
      ];
      this.form.memo = d.memo ?? '';

      // 添付ファイル
      const atts: any[] = d.attachments ?? [];
      this.attachments.eval       = atts.filter(a => a.fileCategory === 1).map(this.mapAttachment);
      this.attachments.image      = atts.filter(a => a.fileCategory === 2).map(this.mapAttachment);
      this.attachments.deliverable = atts.filter(a => a.fileCategory === 3).map(this.mapAttachment);
      this.attachments.reportDoc  = atts.filter(a => a.fileCategory === 4).map(this.mapAttachment);
      this.form.targetOrgDetail        = d.targetOrgDetail        ?? [];
    });
  }

  private mapAttachment(a: any): AttachmentItem {
    return { fileName: a.fileName, filePath: a.filePath, fileKey: '', downloadUrl: a.downloadUrl };
  }

  private applyDefaults(): void {
    // 企画書自動反映フィールドの名称解決（マスタロード後に実行）
    // my-event-detail と同様に categoryId ↔ categoryMajorName を双方向解決
    if (this.form.categoryId !== null) {
      const major = this.getMasterItems('category_major').find(m => m.id === this.form.categoryId);
      if (major) this.form.categoryMajorName = major.name;
    } else if (this.form.categoryMajorName) {
      const major = this.getMasterItems('category_major').find(m => m.name === this.form.categoryMajorName);
      if (major) this.form.categoryId = major.id;
    }
    this.form.categorySub = this._rawCategorySub
      .filter((id): id is number => id !== null)
      .map(id => this.getMasterItems('category_sub').find(m => m.id === id)?.name ?? '');

    if (this._rawImprovementOutcome !== null) {
      const outcome = this.getMasterItems('improvement_indicator_outcome').find(m => m.id === this._rawImprovementOutcome);
      this.form.improvementOutcomeText = outcome?.name ?? '';
    }
    this.form.improvementStateItems = this._rawImprovementState
      .filter((id): id is number => id !== null)
      .map(id => this.getMasterItems('improvement_indicator_state').find(m => m.id === id)?.name ?? '')
      .filter(name => name !== '');

    // 実施状況：未設定の場合は先頭（実施）をデフォルト
    if (this.form.implementationStatus === null) {
      const statusItems = this.getMasterItems('report_implementation_status');
      if (statusItems.length > 0) this.form.implementationStatus = statusItems[0].id;
    }
    // 協議状況：未設定の場合は先頭（合意）をデフォルト
    if (this.form.negotiationStatus === null) {
      const negotiationItems = this.getMasterItems('report_negotiation_status');
      if (negotiationItems.length > 0) {
        this.form.negotiationStatus = negotiationItems[0].id;
        this._agreementContentWasVisible = true;
      }
    }

    // 実施方法：報告書未設定の場合は企画書の値をデフォルトとして使用
    if (this.form.implementationMethod === null && this._planImplementationMethod !== null) {
      this.form.implementationMethod = this._planImplementationMethod;
    }

    // 参加者評価点数：デフォ「3 普通」（ブランクなし）
    if (this.form.participantEvalScore === null) {
      const evalItems = this.getMasterItems('report_eval_score');
      const futsuu = evalItems.find(m => m.attribute === 'default');
      if (futsuu) this.form.participantEvalScore = futsuu.id;
      else if (evalItems.length >= 3) this.form.participantEvalScore = evalItems[2].id;
    }
    // 改善指標に対する結果：デフォ「予定どおり改善が見込める」（先頭）
    if (this.form.improvementResultRating === null) {
      const resultItems = this.getMasterItems('report_improvement_result');
      if (resultItems.length > 0) this.form.improvementResultRating = resultItems[0].id;
    }
    // 参加者理解度：デフォ「5 十分理解できた」（先頭）
    if (this.form.understandingScore === null) {
      const understandItems = this.getMasterItems('report_understanding_score');
      if (understandItems.length > 0) this.form.understandingScore = understandItems[0].id;
    }

    if (this.form.targetOrgDetail.length > 0) {
      this.targetSelectedNodes = this.buildSelectedNodesFromIds(this.form.targetOrgDetail);
    }
  }

  private validateRequired(): string[] {
    if (this.isLimitedDisplay) return [];

    const errors: string[] = [];
    if (!this.form.implementationMethod) errors.push('実施方法');
    if (this.showStakeholderAndChallenges && !this.form.stakeholderInfo) errors.push('関係者情報');
    if (this.showCost && this.form.categoryId !== 10 && this.form.actualTotalCost === null) errors.push('実費費用総額');
    if (this.showTotalParticipants && this.form.participantTotal === null) errors.push('参加者 総数');
    if (this.showParticipantDetail) {
      if (this.form.participantNew === null) errors.push('参加者 新規');
      if (this.form.femaleRatio === null) errors.push('参加者 女性比率');
    }
    if (this.showOrgActivity && !this.form.implementationContent) errors.push('実施内容');
    if (this.showOutcomeResult && !this.form.implementationResult) errors.push('実施結果/成果');
    if (this.showNextImprovement && !this.form.nextImprovement) errors.push('次回改善点/引き継ぎ事項');
    if (this.showLaborNegotiation) {
      if (this.showAgreementContent && !this.form.agreementContent) errors.push('合意内容');
      if (!this.form.companyClaim) errors.push('会社側主張/論理');
      if (!this.form.unionClaim) errors.push('労組側主張/論理');
    }
    if (this.showImprovementResult && this.form.improvementResultRating === null) errors.push('改善指標に対する結果');
    if (this.showEvaluation) {
      if (this.form.participantEvalScore === null) errors.push('参加者 評価 点数');
      if (!this.isEvaluationUnrated && this.form.evalCollectedCount === null) errors.push('評価収集数');
      if (this.isEvaluationUnrated && !this.form.unevaluatedReason) errors.push('未評価理由');
    }
    if (this.form.unionReportMethod.every(v => v === null)) errors.push('組合員向け報告方法');

    return errors;
  }

  private buildPayload(): object {
    const isLtd = this.isLimitedDisplay;
    console.log(this.coverageRateValue)

    return {
      implementationStatus:  this.form.implementationStatus,
      implementationMethod:  isLtd ? null : this.form.implementationMethod,
      hasProblem:            this.showProblem ? this.form.hasProblem : null,
      problemComment:        this.showProblem ? this.form.problemComment : '',
      stakeholderInfo:       (!isLtd && this.showStakeholderAndChallenges) ? this.form.stakeholderInfo : '',
      hasChangeFromPlan:     this.form.hasChangeFromPlan,
      changeFromPlanComment: this.form.changeFromPlanComment,
      actualTotalCost:       (!isLtd && this.showCost) ? this.form.actualTotalCost : null,
      costDiffReason:        (!isLtd && this.showCost) ? this.form.costDiffReason : '',
      participantTotal:      (!isLtd && this.showTotalParticipants) ? this.form.participantTotal : null,
      participantNew:        (!isLtd && this.showParticipantDetail) ? this.form.participantNew : null,
      ageRatioUnder19:       (!isLtd && this.showParticipantDetail) ? this.form.ageRatio.under19 : null,
      ageRatio20s:           (!isLtd && this.showParticipantDetail) ? this.form.ageRatio.age20s  : null,
      ageRatio30s:           (!isLtd && this.showParticipantDetail) ? this.form.ageRatio.age30s  : null,
      ageRatio40s:           (!isLtd && this.showParticipantDetail) ? this.form.ageRatio.age40s  : null,
      ageRatio50s:           (!isLtd && this.showParticipantDetail) ? this.form.ageRatio.age50s  : null,
      ageRatioOver60:        (!isLtd && this.showParticipantDetail) ? this.form.ageRatio.over60  : null,
      ageRatioUnknown:       (!isLtd && this.showParticipantDetail) ? this.form.ageRatio.unknown : false,
      femaleRatio:           (!isLtd && this.showParticipantDetail) ? this.form.femaleRatio : null,
      implementationContent: (!isLtd && this.showOrgActivity)       ? this.form.implementationContent : '',
      implementationResult:  (!isLtd && this.showOutcomeResult)     ? this.form.implementationResult  : '',
      challengesNotes:       (!isLtd && this.showStakeholderAndChallenges) ? this.form.challengesNotes : '',
      nextImprovement:       (!isLtd && this.showNextImprovement)   ? this.form.nextImprovement : '',
      negotiationStatus:     (!isLtd && this.showLaborNegotiation)  ? this.form.negotiationStatus : null,
      agreementContent:      (!isLtd && this.showAgreementContent)  ? this.form.agreementContent : '',
      companyClaim:          (!isLtd && this.showLaborNegotiation)  ? this.form.companyClaim : '',
      unionClaim:            (!isLtd && this.showLaborNegotiation)  ? this.form.unionClaim : '',
      unresolvedPoints:      (!isLtd && this.showLaborNegotiation)  ? this.form.unresolvedPoints : '',
      improvementResultRating: (!isLtd && this.showImprovementResult) ? this.form.improvementResultRating : null,
      improvementResultReason: (!isLtd && this.showImprovementResult) ? this.form.improvementResultReason : '',
      reAttendanceRate:      (!isLtd && this.showEvaluation) ? this.form.reAttendanceRate : null,
      participantEvalScore:  (!isLtd && this.showEvaluation) ? this.form.participantEvalScore : null,
      evalCollectedCount:    (!isLtd && this.showEvaluation && !this.isEvaluationUnrated) ? this.form.evalCollectedCount : null,
      unevaluatedReason:     (!isLtd && this.showEvaluation && this.isEvaluationUnrated) ? this.form.unevaluatedReason : '',
      evaluationPart1Title:  (!isLtd && this.showParticipantDetail) ? this.form.evaluationPart1.title : '',
      evaluationPart1Score:  (!isLtd && this.showParticipantDetail) ? this.form.evaluationPart1.score : null,
      evaluationPart2Title:  (!isLtd && this.showParticipantDetail) ? this.form.evaluationPart2.title : '',
      evaluationPart2Score:  (!isLtd && this.showParticipantDetail) ? this.form.evaluationPart2.score : null,
      understandingScore:    (!isLtd && this.showUnderstanding) ? this.form.understandingScore : null,
      participantOpinions:   (!isLtd && this.showEvaluation) ? this.form.participantOpinions : '',
      selfEvalScore:         (!isLtd && this.showEvaluation) ? this.form.selfEvalScore : null,
      selfEvalComment:       (!isLtd && this.showEvaluation) ? this.form.selfEvalComment : '',
      canRedo:               (!isLtd && this.showEvaluation) ? this.form.canRedo : null,
      unionReportMethod1:    !isLtd ? this.form.unionReportMethod[0] : null,
      unionReportMethod2:    !isLtd ? this.form.unionReportMethod[1] : null,
      unionReportMethod3:    !isLtd ? this.form.unionReportMethod[2] : null,
      memo: this.form.memo,
      attachments: isLtd ? [] : [
        ...this.attachments.eval.map((a, i)        => ({ fileCategory: 1, fileName: a.fileName, filePath: a.filePath, sortOrder: i + 1 })),
        ...this.attachments.image.map((a, i)       => ({ fileCategory: 2, fileName: a.fileName, filePath: a.filePath, sortOrder: i + 1 })),
        ...this.attachments.deliverable.map((a, i) => ({ fileCategory: 3, fileName: a.fileName, filePath: a.filePath, sortOrder: i + 1 })),
        ...this.attachments.reportDoc.map((a, i)   => ({ fileCategory: 4, fileName: a.fileName, filePath: a.filePath, sortOrder: i + 1 })),
      ],
      coverageRate: this.coverageRateValue?.toString().trim() === '' ? null : this.coverageRateValue,
    };
  }

  private createEmptyForm(): EventReportForm {
    return {
      status: 'draft',
      title: '',
      categoryId: null,
      categoryMajorName: '',
      categorySub: [],
      estimatedTotalCost: null,
      improvementOutcomeText: '',
      improvementStateItems: [],
      implementationStatus: null,
      implementationMethod: null,
      hasProblem: true,
      problemComment: '',
      stakeholderInfo: '',
      hasChangeFromPlan: true,
      changeFromPlanComment: '',
      actualTotalCost: null,
      costDiffReason: '',
      participantTotal: null,
      participantNew: null,
      ageRatio: this.createEmptyAgeRatio(),
      femaleRatio: null,
      implementationContent: '',
      implementationResult: '',
      challengesNotes: '',
      nextImprovement: '',
      negotiationStatus: null,
      agreementContent: '',
      companyClaim: '',
      unionClaim: '',
      unresolvedPoints: '',
      improvementResultRating: null,
      improvementResultReason: '',
      reAttendanceRate: null,
      participantEvalScore: null,
      evalCollectedCount: null,
      unevaluatedReason: '',
      evaluationPart1: { title: '', score: null },
      evaluationPart2: { title: '', score: null },
      understandingScore: null,
      participantOpinions: '',
      selfEvalScore: null,
      selfEvalComment: '',
      canRedo: null,
      unionReportMethod: [null, null, null],
      memo: '',
      targetOrgDetail: [],
      lastUpdatedName: null,
      lastUpdatedOfficialPositionName: null,
      lastUpdatedAt: null,
    };
  }

  private createEmptyAgeRatio(): ParticipantAgeRatio {
    return { under19: null, age20s: null, age30s: null, age40s: null, age50s: null, over60: null, unknown: false };
  }
}
